/**
 * AudioMixer - Professional mixing board UI for audio tracks
 * Features: Volume faders (dB), Pan knobs, Mute/Solo buttons
 */
import { useState, useCallback, useMemo } from 'react';
import { X, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

// Convert dB to linear gain
const dbToGain = (db) => {
  if (db <= -60) return 0;
  return Math.pow(10, db / 20);
};

// Convert linear gain to dB
const gainToDb = (gain) => {
  if (gain <= 0) return -60;
  return 20 * Math.log10(gain);
};

// Format dB for display
const formatDb = (db) => {
  if (db <= -60) return '-∞';
  return db.toFixed(1);
};

// Track colors for visual distinction
const TRACK_COLORS = [
  { bg: 'bg-green-500', icon: 'bg-green-400' },
  { bg: 'bg-blue-500', icon: 'bg-blue-400' },
  { bg: 'bg-purple-500', icon: 'bg-purple-400' },
  { bg: 'bg-pink-500', icon: 'bg-pink-400' },
  { bg: 'bg-yellow-500', icon: 'bg-yellow-400' },
  { bg: 'bg-cyan-500', icon: 'bg-cyan-400' },
  { bg: 'bg-orange-500', icon: 'bg-orange-400' },
  { bg: 'bg-red-500', icon: 'bg-red-400' },
];

// Pan Knob Component
function PanKnob({ value = 0, onChange }) {
  const rotation = value * 135; // -135 to 135 degrees
  
  return (
    <div className="relative w-10 h-10 flex items-center justify-center">
      {/* Knob background */}
      <div className="absolute w-9 h-9 rounded-full bg-zinc-700 border-2 border-zinc-600 shadow-inner" />
      
      {/* Knob indicator */}
      <div 
        className="absolute w-9 h-9 rounded-full flex items-center justify-center"
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        <div className="absolute top-1 w-0.5 h-2 bg-white rounded-full" />
      </div>
      
      {/* Interactive area */}
      <input
        type="range"
        min="-100"
        max="100"
        value={value * 100}
        onChange={(e) => onChange(parseInt(e.target.value) / 100)}
        className="absolute w-full h-full opacity-0 cursor-pointer"
        title={`Pan: ${value < 0 ? 'L' : value > 0 ? 'R' : 'C'} ${Math.abs(value * 100).toFixed(0)}%`}
      />
      
      {/* L/R labels */}
      <span className="absolute -left-3 text-[8px] text-zinc-500">L</span>
      <span className="absolute -right-3 text-[8px] text-zinc-500">R</span>
    </div>
  );
}

// Volume Fader Component
function VolumeFader({ value = 0, onChange, muted = false }) {
  // value is in dB, range -60 to +12
  const sliderValue = ((value + 60) / 72) * 100; // Convert to 0-100
  
  const handleChange = (newValue) => {
    const db = (newValue / 100) * 72 - 60; // Convert back to dB
    onChange(Math.round(db * 10) / 10);
  };
  
  return (
    <div className="flex flex-col items-center h-64 relative">
      {/* dB scale */}
      <div className="absolute left-0 h-full flex flex-col justify-between text-[8px] text-zinc-500 -ml-4">
        <span>+12</span>
        <span>0</span>
        <span>-12</span>
        <span>-24</span>
        <span>-48</span>
        <span>-∞</span>
      </div>
      
      {/* Fader track */}
      <div className="relative w-8 h-full bg-zinc-800 rounded overflow-hidden">
        {/* Level meter background */}
        <div className="absolute inset-0 flex flex-col">
          <div className="flex-1 bg-gradient-to-b from-red-900/30 to-yellow-900/30" />
          <div className="flex-[2] bg-gradient-to-b from-yellow-900/30 to-green-900/30" />
          <div className="flex-[3] bg-gradient-to-b from-green-900/30 to-green-950/30" />
        </div>
        
        {/* Unity gain marker (0 dB) */}
        <div className="absolute w-full h-px bg-zinc-500 top-[16.7%]" />
        
        {/* Fader handle */}
        <input
          type="range"
          min="0"
          max="100"
          value={muted ? 0 : sliderValue}
          onChange={(e) => handleChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-ns-resize"
          style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
        />
        
        {/* Visual fader position */}
        <div 
          className="absolute w-full transition-all duration-75"
          style={{ 
            bottom: `${muted ? 0 : sliderValue}%`,
            height: '4px'
          }}
        >
          <div className={`w-full h-full ${muted ? 'bg-zinc-600' : 'bg-zinc-300'} rounded shadow-lg`} />
        </div>
        
        {/* Fill below fader */}
        <div 
          className={`absolute bottom-0 w-full transition-all duration-75 ${muted ? 'bg-zinc-700' : 'bg-green-500/50'}`}
          style={{ height: `${muted ? 0 : sliderValue}%` }}
        />
      </div>
      
      {/* Current value display */}
      <div className="mt-2 text-[10px] text-zinc-400 font-mono">
        {muted ? '-∞' : formatDb(value)} dB
      </div>
    </div>
  );
}

// Single Channel Strip
function ChannelStrip({ 
  track, 
  index, 
  isSoloed,
  anySoloed,
  onVolumeChange, 
  onPanChange, 
  onMuteToggle,
  onSoloToggle 
}) {
  const color = TRACK_COLORS[index % TRACK_COLORS.length];
  const isMuted = track.muted || (anySoloed && !isSoloed);
  const volume = track.volume ?? 0;
  const pan = track.pan ?? 0;
  
  return (
    <div className="flex flex-col items-center p-2 bg-zinc-850 rounded-lg border border-zinc-700 min-w-[80px]">
      {/* Track icon */}
      <div className={`w-10 h-10 rounded-lg ${color.icon} flex items-center justify-center mb-2`}>
        <Volume2 className="w-5 h-5 text-white" />
      </div>
      
      {/* Pan knob */}
      <PanKnob value={pan} onChange={(v) => onPanChange(track.id, v)} />
      <span className="text-[9px] text-zinc-500 mt-1 mb-2">
        {pan === 0 ? 'C' : pan < 0 ? `L${Math.abs(Math.round(pan * 100))}` : `R${Math.round(pan * 100)}`}
      </span>
      
      {/* Volume fader */}
      <VolumeFader 
        value={volume} 
        onChange={(v) => onVolumeChange(track.id, v)}
        muted={isMuted}
      />
      
      {/* Mute/Solo buttons */}
      <div className="flex gap-1 mt-2">
        <button
          onClick={() => onMuteToggle(track.id)}
          className={`w-7 h-6 rounded text-xs font-bold transition-colors ${
            track.muted 
              ? 'bg-red-500 text-white' 
              : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
          }`}
        >
          M
        </button>
        <button
          onClick={() => onSoloToggle(track.id)}
          className={`w-7 h-6 rounded text-xs font-bold transition-colors ${
            isSoloed 
              ? 'bg-yellow-500 text-black' 
              : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
          }`}
        >
          S
        </button>
      </div>
      
      {/* Track name */}
      <div className={`mt-2 px-2 py-1 ${color.bg} rounded text-[10px] text-white font-medium truncate max-w-full`}>
        {track.name || `Track ${index + 1}`}
      </div>
    </div>
  );
}

// Master Channel Strip
function MasterStrip({ volume = 0, onVolumeChange }) {
  return (
    <div className="flex flex-col items-center p-2 bg-zinc-800 rounded-lg border border-purple-500/50 min-w-[80px]">
      {/* Master icon */}
      <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center mb-2">
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
      </div>
      
      {/* Spacer to align with other strips */}
      <div className="h-10 mb-2" />
      <span className="text-[9px] text-zinc-500 mb-2">Master</span>
      
      {/* Volume fader */}
      <VolumeFader 
        value={volume} 
        onChange={onVolumeChange}
        muted={false}
      />
      
      {/* Mute/Dim buttons */}
      <div className="flex gap-1 mt-2">
        <button className="w-7 h-6 rounded text-xs font-bold bg-zinc-700 text-zinc-400 hover:bg-zinc-600">
          M
        </button>
        <button className="w-7 h-6 rounded text-xs font-bold bg-zinc-700 text-zinc-400 hover:bg-zinc-600">
          D
        </button>
      </div>
      
      {/* Label */}
      <div className="mt-2 px-2 py-1 bg-purple-600 rounded text-[10px] text-white font-medium">
        Master
      </div>
    </div>
  );
}

// Main Mixer Component
export function AudioMixer({ 
  audioTracks = [], 
  masterVolume = 0,
  onTrackVolumeChange,
  onTrackPanChange,
  onTrackMuteToggle,
  onMasterVolumeChange,
  onClose 
}) {
  const [soloedTracks, setSoloedTracks] = useState(new Set());
  
  const handleSoloToggle = useCallback((trackId) => {
    setSoloedTracks(prev => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  }, []);
  
  const anySoloed = soloedTracks.size > 0;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl max-w-[90vw] max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 bg-zinc-850">
          <h2 className="text-lg font-semibold text-white">Audio Mixer</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Mixer body */}
        <div className="p-4 overflow-x-auto">
          <div className="flex gap-2 items-end">
            {/* Audio tracks */}
            {audioTracks.map((track, index) => (
              <ChannelStrip
                key={track.id}
                track={track}
                index={index}
                isSoloed={soloedTracks.has(track.id)}
                anySoloed={anySoloed}
                onVolumeChange={onTrackVolumeChange}
                onPanChange={onTrackPanChange}
                onMuteToggle={onTrackMuteToggle}
                onSoloToggle={handleSoloToggle}
              />
            ))}
            
            {/* Separator */}
            <div className="w-px h-80 bg-zinc-700 mx-2" />
            
            {/* Master fader */}
            <MasterStrip 
              volume={masterVolume}
              onVolumeChange={onMasterVolumeChange}
            />
          </div>
        </div>
        
        {/* Footer with info */}
        <div className="px-4 py-2 border-t border-zinc-700 bg-zinc-850 text-[10px] text-zinc-500">
          Tip: Use Solo (S) to isolate tracks • Pan knobs: drag left/right • Volume: 0 dB = unity gain
        </div>
      </div>
    </div>
  );
}

export default AudioMixer;
